import clsx from 'clsx';
import { Switch } from 'radix-ui';

type ToggleSwitchProps = {
  /** Optional id for linking labels with htmlFor */
  id?: string;
  /** Current switch state */
  checked: boolean;
  /** Called when switch state changes */
  onCheckedChange: (isChecked: boolean) => void;
  /** Disables interactions when true */
  disabled?: boolean;
  /** Screen-reader label */
  ariaLabel: string;
  /** Size variant (defaults to 'default') */
  size?: 'default' | 'small';
};

const ToggleSwitch = ({ id, checked, onCheckedChange, disabled, ariaLabel, size = 'default' }: ToggleSwitchProps) => {
  const isSmall = size === 'small';

  return (
    <Switch.Root
      className={clsx(
        'group relative shrink-0 rounded-full shadow-xs',
        'box-border border border-transparent outline-none transition-all',
        'focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:data-[state=checked]:bg-muted-foreground/40 disabled:data-[state=unchecked]:bg-muted-foreground/40',
        // Size variants
        isSmall ? 'h-4 w-7' : 'h-5 w-9',
        // Unchecked styles
        'not-disabled:border-border not-disabled:data-[state=unchecked]:bg-background',
        // Checked styles
        'data-[state=checked]:bg-primary',
      )}
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      <Switch.Thumb
        className={clsx(
          'pointer-events-none absolute block rounded-full bg-background shadow-lg ring-0 transition-transform will-change-transform',
          'not-disabled:data-[state=unchecked]:bg-primary group-disabled:data-[state=checked]:bg-muted group-disabled:data-[state=unchecked]:bg-muted-foreground/80',
          isSmall ? 'top-px size-3 translate-x-px' : 'top-0.5 size-3.5 translate-x-0.5',
          isSmall
            ? 'not-disabled:data-[state=checked]:translate-x-3.5 not-disabled:data-[state=unchecked]:translate-x-px'
            : 'not-disabled:data-[state=checked]:translate-x-4.5 not-disabled:data-[state=unchecked]:translate-x-0.5',
        )}
      />
    </Switch.Root>
  );
};

export { ToggleSwitch };
